import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import api, { getErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// App Store requirement: in-app account deletion (password + typed DELETE confirmation).
const DeleteAccount = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/account/delete", { password, confirm });
      setOpen(false);
      localStorage.removeItem("tycc_family_v2");
      localStorage.removeItem("tycc_progress_v1");
      logout();
      navigate("/");
      toast.success("Your account has been deleted", { description: "All family data, photos and recordings were removed. Thank you for learning with us." });
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not delete the account"));
    } finally { setBusy(false); }
  };

  return (
    <section id="delete-account" className="mt-10 rounded-3xl border-2 border-[#fecdd3] bg-[#fff1f2] p-6" data-testid="delete-account-section">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="w-12 h-12 rounded-2xl bg-white text-[#e11d48] flex items-center justify-center flex-shrink-0"><Trash2 /></span>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-[#9f1239]" style={{ fontFamily: "Literata, serif" }}>Delete account</h2>
          <p className="text-sm text-[#9f1239]/80 mt-1">Permanently removes your account, every child profile, progress, cheers, photos, voice notes and pep talks. This cannot be undone. See our <a href="/privacy" className="underline font-semibold">privacy policy</a>.</p>
        </div>
        <button type="button" onClick={() => setOpen(true)} className="btn-pill bg-white border-2 border-[#e11d48] text-[#e11d48] hover:bg-[#e11d48] hover:text-white text-sm inline-flex items-center gap-2" data-testid="delete-account-button"><Trash2 size={16} /> Delete my account</button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPassword(""); setConfirm(""); } }}>
        <DialogContent className="rounded-3xl" data-testid="delete-account-dialog">
          <DialogTitle className="text-[#9f1239] inline-flex items-center gap-2" style={{ fontFamily: "Literata, serif" }}><AlertTriangle size={20} /> Delete everything?</DialogTitle>
          <DialogDescription>This deletes the account for <b>{user.email}</b> and all children, progress, cheers, photos and recordings. Co-parents and followers lose access immediately.</DialogDescription>
          <form onSubmit={submit} className="mt-2 space-y-3">
            <label className="block text-sm font-bold text-gray-700">Your password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="mt-1 w-full h-11 rounded-2xl px-4 border-2 border-[#fecdd3] focus:border-[#e11d48] focus:outline-none bg-white" data-testid="delete-account-password" />
            </label>
            <label className="block text-sm font-bold text-gray-700">Type <span className="font-mono text-[#e11d48]">DELETE</span> to confirm
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="off" className="mt-1 w-full h-11 rounded-2xl px-4 border-2 border-[#fecdd3] focus:border-[#e11d48] focus:outline-none bg-white font-mono" data-testid="delete-account-confirm" />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setOpen(false)} className="btn-pill bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm" data-testid="delete-account-cancel">Keep my account</button>
              <button type="submit" disabled={busy || confirm.trim().toUpperCase() !== "DELETE" || !password} className="btn-pill bg-[#e11d48] hover:bg-[#be123c] text-white text-sm disabled:opacity-50" data-testid="delete-account-submit">{busy ? "Deleting..." : "Delete permanently"}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default DeleteAccount;
